import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApiClient } from '@/api/client'
import { useWorkspace } from '@/workspaces/WorkspaceContext'

export type StatusCategory = 'todo' | 'in_progress' | 'done'

export type SchemeStatus = {
  slug: string
  label: string
  color: string
  category: StatusCategory
  order: number
}

type StatusSchemeRaw = {
  id: string
  workspace_id: string
  name: string
  applies_to: 'task' | 'project'
  scope: string
  is_default: boolean
  statuses: SchemeStatus[]
  created_at: string
  updated_at: string
}

const STATUS_COLOR_FALLBACKS: Record<string, string> = {
  open: '#8A8F98',
  new: '#8A8F98',
  in_progress: '#5B8DEF',
  review: '#9B7AD6',
  done: '#58B783',
  closed: '#58B783',
  canceled: '#9A7A63',
  cancelled: '#9A7A63',
  gray: '#8A8F98',
  slate: '#9A7A63',
  indigo: '#5B8DEF',
  blue: '#5B8DEF',
  amber: '#9B7AD6',
  emerald: '#58B783',
  green: '#58B783',
  rose: '#9A7A63',
}

function normalizeStatusColor(status: SchemeStatus): SchemeStatus {
  const color = status.color?.trim()
  return {
    ...status,
    color: STATUS_COLOR_FALLBACKS[color] ?? color ?? STATUS_COLOR_FALLBACKS[status.slug] ?? '#8A8F98',
  }
}

export function useStatusScheme(appliesTo: 'task' | 'project') {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const slug = workspace.slug

  const query = useQuery({
    queryKey: ['status-schemes', slug, appliesTo],
    queryFn: async () => {
      const res = await fetchWithAuth<{ items: StatusSchemeRaw[] }>(
        `/api/v1/workspaces/${slug}/status-schemes?applies_to=${appliesTo}`,
      )
      return (
        res.items.find((s) => s.is_default && s.scope === 'workspace') ??
        res.items[0] ??
        null
      )
    },
    select: (scheme) => scheme ? {
      ...scheme,
      statuses: scheme.statuses.map(normalizeStatusColor),
    } : null,
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  })

  const statusesBySlug = useMemo(() => {
    const map = new Map<string, SchemeStatus>()
    for (const s of query.data?.statuses ?? []) map.set(s.slug, s)
    return map
  }, [query.data])

  return {
    scheme: query.data ?? null,
    statuses: query.data?.statuses ?? [],
    statusesBySlug,
    isLoading: query.isLoading,
  }
}
